import React from "react";
import { Link, useLocation } from "react-router-dom";

/**
 * Shared chrome, kept identical to the design drafts: a phone frame, a header, and the five-tab bottom
 * navigation. The desktop layout is deferred in the design, so the web client wears the mobile shape
 * rather than inventing one.
 */

export function Header({
  title,
  back,
  backLabel,
  action
}: {
  title?: string;
  back?: string;
  backLabel?: string;
  action?: React.ReactNode;
}): JSX.Element {
  return (
    <header className="app-header">
      {back ? (
        <Link className="back" to={back}>
          {backLabel ?? "\u2190"}
        </Link>
      ) : null}
      {title ? <h1>{title}</h1> : null}
      {action}
    </header>
  );
}

const TABS = [
  { to: "/timeline", icon: "\u{1F552}", label: "Timeline" },
  { to: "/calls", icon: "\u260E", label: "Calls" },
  { to: "/conversations", icon: "\u{1F4AC}", label: "Chats" },
  { to: "/numbers", icon: "#", label: "Numbers" },
  { to: "/contacts", icon: "\u{1F464}", label: "Contacts" },
  { to: "/menu", icon: "\u2630", label: "Menu" }
];

export function BottomNav(): JSX.Element {
  const { pathname } = useLocation();
  return (
    <nav className="bottom-nav">
      {TABS.map((tab) => (
        <Link key={tab.to} to={tab.to} className={pathname.startsWith(tab.to) ? "active" : undefined}>
          <span className="ic">{tab.icon}</span>
          {tab.label}
        </Link>
      ))}
    </nav>
  );
}

export function StateBadge({ state }: { state: string }): JSX.Element {
  const normalized = state.toLowerCase();
  const className =
    normalized === "active" ? "active" : normalized === "disabled" ? "disabled" : "released";
  return <span className={`badge ${className}`}>{state.charAt(0) + state.slice(1).toLowerCase()}</span>;
}

export function Avatar({ label, square }: { label: string; square?: boolean }): JSX.Element {
  return <div className={square ? "avatar sq" : "avatar"}>{initials(label)}</div>;
}

export function initials(label: string): string {
  const trimmed = label.trim();
  if (trimmed.startsWith("+")) {
    return trimmed.slice(0, 3);
  }
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return "?";
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function Empty({ children }: { children: React.ReactNode }): JSX.Element {
  return <div className="empty">{children}</div>;
}

export function Loading(): JSX.Element {
  return <div className="loading">Loading...</div>;
}

export function ErrorNote({ error }: { error: unknown }): JSX.Element | null {
  if (!error) {
    return null;
  }
  return <div className="error">{error instanceof Error ? error.message : String(error)}</div>;
}

export function Note({ children }: { children: React.ReactNode }): JSX.Element {
  return <div className="note">{children}</div>;
}

export function SectionLabel({ children }: { children: React.ReactNode }): JSX.Element {
  return <div className="section-label">{children}</div>;
}

/** Money as the drafts show it: "29 DKK / month". */
export function formatPrice(amountMinor: number, currency: string): string {
  return `${(amountMinor / 100).toFixed(0)} ${currency} / month`;
}

/** Timestamps as the lists show them: time today, weekday this week, date beyond. */
export function formatWhen(iso: string | null): string {
  if (!iso) {
    return "";
  }
  const when = new Date(iso);
  const now = new Date();
  const sameDay = when.toDateString() === now.toDateString();
  if (sameDay) {
    return when.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  }
  const daysAgo = (now.getTime() - when.getTime()) / 86_400_000;
  if (daysAgo < 7) {
    return when.toLocaleDateString(undefined, { weekday: "short" });
  }
  return when.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
}

export function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds === undefined) {
    return "";
  }
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return minutes > 0 ? `${minutes}m ${rest}s` : `${rest}s`;
}
